import { cn } from "@workspace/ui/lib/utils";
import { Loader2 } from "lucide-react";

type RequestStage =
  | "collecting"
  | "ready"
  | "processing"
  | "completed"
  | "failed"
  | "expired"
  | "rejected";

const STAGE_LABEL: Record<RequestStage, string> = {
  collecting: "Collecting",
  ready: "Ready",
  processing: "Processing",
  completed: "Completed",
  failed: "Failed",
  expired: "Expired",
  rejected: "Rejected",
};

function stageDot(stage: RequestStage): string {
  switch (stage) {
    case "completed":
      return "#22c55e";
    case "processing":
      return "#3b82f6";
    case "ready":
      return "#10b981";
    case "collecting":
      return "#f59e0b";
    default:
      return "#ef4444";
  }
}

export const COMPACT_THRESHOLD = 65;

type ImageCardProps = {
  stage: RequestStage;
  createdAt: number;
  promptText?: string;
  previewUrl?: string;
  onClick?: () => void;
  isSelected?: boolean;
  isCompact?: boolean;
};

function CompactCard({
  stage,
  promptText,
  previewUrl,
  onClick,
  isSelected,
}: Omit<ImageCardProps, "createdAt" | "isCompact">) {
  const dot = stageDot(stage);
  const truncatedPrompt = promptText
    ? promptText.split(" ").slice(0, 3).join(" ")
    : "Untitled";

  return (
    <button
      className={cn(
        "relative z-0 flex flex-col overflow-hidden bg-selca-card-bg text-left transition-all duration-200",
        "hover:scale-[1.03] hover:shadow-xl hover:z-10 cursor-pointer",
        "rounded-[5%]",
        isSelected
          ? "ring-2 ring-selca-cocoa/50 z-10 shadow-lg"
          : "ring-1 ring-selca-card-border"
      )}
      onClick={onClick}
      type="button"
    >
      <div className="relative aspect-square w-full overflow-hidden">
        {previewUrl ? (
          <img alt="" className="size-full object-cover" src={previewUrl} />
        ) : (
          <div className="grid size-full place-items-center bg-selca-cocoa/5 dark:bg-amber-950/40">
            <svg
              className="size-1/3 text-selca-cocoa/20"
              fill="none"
              stroke="currentColor"
              strokeWidth={1}
              viewBox="0 0 24 24"
            >
              <path
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-1.5 px-2 py-1.5 bg-selca-card-bg/80 backdrop-blur-sm">
        <span className="truncate text-[10px] font-medium text-selca-cocoa/80 leading-tight">
          {truncatedPrompt}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          {stage === "processing" && (
            <Loader2 className="size-2.5 animate-spin text-selca-cocoa/60" />
          )}
          <span
            className="size-2 shrink-0 rounded-full shadow-sm"
            style={{ background: dot }}
            title={STAGE_LABEL[stage]}
          />
        </div>
      </div>
    </button>
  );
}

function FullCard({
  stage,
  createdAt,
  promptText,
  previewUrl,
  onClick,
  isSelected,
}: Omit<ImageCardProps, "isCompact">) {
  const dot = stageDot(stage);

  return (
    <button
      className={cn(
        "relative z-0 rounded-md border bg-selca-card-bg text-left transition-all duration-200",
        "hover:scale-[1.02] hover:shadow-xl hover:z-10 cursor-pointer",
        isSelected
          ? "border-selca-cocoa ring-2 ring-selca-cocoa/30 z-10"
          : "border-selca-card-border"
      )}
      onClick={onClick}
      type="button"
    >
      <div className="flex items-center justify-between px-2 py-1.5">
        <div className="inline-flex items-center gap-1.5 rounded-sm border border-selca-card-border bg-zinc-100 px-2 py-0.5 text-sm font-medium text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100">
          <span className="size-2 rounded-full" style={{ background: dot }} />
          {STAGE_LABEL[stage]}
          {stage === "processing" && (
            <Loader2 className="size-3 animate-spin" />
          )}
        </div>
        <div className="font-mono text-xs text-selca-cocoa/60">
          {new Date(createdAt).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>

      <div className="px-2 pb-2">
        <div className="aspect-4/3 overflow-hidden rounded border border-selca-card-border bg-selca-cocoa/5 dark:bg-amber-950/40">
          {previewUrl ? (
            <img alt="" className="size-full object-cover" src={previewUrl} />
          ) : (
            <div className="grid size-full place-items-center text-[10px] text-selca-cocoa/40">
              No preview
            </div>
          )}
        </div>

        <div className="mt-1.5 line-clamp-1 text-[11px] font-medium text-selca-cocoa">
          {promptText ?? "Untitled request"}
        </div>
      </div>
    </button>
  );
}

export function ImageCard({
  stage,
  createdAt,
  promptText,
  previewUrl,
  onClick,
  isSelected,
  isCompact = false,
}: ImageCardProps) {
  if (isCompact) {
    return (
      <CompactCard
        isSelected={isSelected}
        onClick={onClick}
        previewUrl={previewUrl}
        promptText={promptText}
        stage={stage}
      />
    );
  }

  return (
    <FullCard
      createdAt={createdAt}
      isSelected={isSelected}
      onClick={onClick}
      previewUrl={previewUrl}
      promptText={promptText}
      stage={stage}
    />
  );
}
